import { getNotesDetails } from "../service/notes.service";
import { clearBuffers, estimateStorage } from "./audioStore";
import { logStorageQuotaRecovery } from "./audioStoreLogger";

/**
 * StorageQuotaError recovery for the @nudge-ai/audio-store local audio backup.
 *
 * When both local tiers (Cache Storage + IndexedDB) hit the shared per-origin
 * quota, saveChunk throws a typed `StorageQuotaError`. Heavy clinicians who
 * record for hours accumulate a local copy of EVERY recording; those copies are
 * only needed until the audio is durably in GCS, after which they are dead bytes
 * that eventually exhaust the origin quota (Sentry NUDGE-FRONTEND-3AV).
 *
 * On a quota error we reclaim space by clearing the local backup of recordings
 * the BACKEND confirms are fully processed (status "Complete" === 1) and durable
 * in GCS. The package cannot do this itself — it has no idea which recordings
 * were submitted — so the decision lives here.
 *
 * Safety contract (the whole point of the local backup is recovering audio that
 * never reached GCS):
 *   - We NEVER clear the recording currently being captured.
 *   - We clear an audioId ONLY when /notes/fetch/<id>/ returns status === 1
 *     (Complete) AND error_status === 0. Any uncertainty (network error, 404,
 *     in-flight/uploading status, processing error) is treated as "keep".
 *   - Failure is silent: we never surface an error to the user (see
 *     feedback: prefer silent audio-upload failures) and never throw back into
 *     the recording path.
 */

// Backend AudioFile status meaning "fully processed & durable in GCS".
// Mirrors the numeric status enum used app-wide (ClinicianNotes getStatusLabel:
// 0 Processing, 1 Complete, 2 Uploading, 3 Transcribing, 4 Generating, 5 Canceled).
const STATUS_DONE_PROCESSING = 1;

// audio-store storage layout — mirrors the package's *versioned* public storage
// contract (frozen for v1/v2 cross-compat):
//   - Cache Storage: one named cache per audioId, "audio-store:v1:<audioId>".
//   - IndexedDB:     DB "AudioBufferDB" (v1), store "audioBuffers",
//                    index "audioId_ts" with key [audioId, ts].
// If the package ever changes this schema, enumeration degrades to "found
// nothing" (a no-op) — it can never cause a wrong delete.
const CACHE_PREFIX = "audio-store:v1:";
const IDB_NAME = "AudioBufferDB";
const IDB_STORE = "audioBuffers";
const IDB_INDEX = "audioId_ts";

// The quota error fires once PER chunk (~every 10s). Single-flight + cooldown so
// a wedged session runs at most one recovery sweep instead of one per chunk.
const RECOVERY_COOLDOWN_MS = 60_000;
// Cap concurrent note-status lookups so a large local backlog can't fan out
// hundreds of API requests in one tick.
const STATUS_LOOKUP_CONCURRENCY = 4;

let recoveryInFlight: Promise<QuotaRecoveryResult> | null = null;
let lastRecoveryAt = 0;

export interface QuotaRecoveryResult {
  /** Candidate audioIds inspected (all local ids except the active recording). */
  attempted: number;
  /** audioIds whose local bytes were cleared (backend-confirmed done-processing). */
  cleared: string[];
  /** Candidates kept because they were not confirmed safe to clear. */
  skipped: number;
  /** Bytes reclaimed per navigator.storage.estimate(), or null if unavailable. */
  reclaimedBytes: number | null;
  /** false when this call was coalesced/suppressed by the cooldown guard. */
  ranSweep: boolean;
}

/** True when the error is the audio-store typed quota error (name is reliable). */
export function isStorageQuotaError(error: unknown): boolean {
  return !!error && typeof error === "object" && (error as { name?: string }).name === "StorageQuotaError";
}

async function listCacheAudioIds(): Promise<string[]> {
  try {
    if (typeof caches === "undefined" || !caches?.keys) return [];
    const names = await caches.keys();
    return names
      .filter((name) => name.startsWith(CACHE_PREFIX))
      .map((name) => name.slice(CACHE_PREFIX.length))
      .filter(Boolean);
  } catch {
    return [];
  }
}

async function listIdbAudioIds(): Promise<string[]> {
  try {
    if (typeof indexedDB === "undefined") return [];

    // Never CREATE the DB. If it does not exist there are no buffered chunks,
    // and creating an empty "AudioBufferDB" with no object store would break the
    // package's own open() (same version => no onupgradeneeded => missing store).
    if (typeof indexedDB.databases === "function") {
      try {
        const dbs = await indexedDB.databases();
        if (!dbs.some((db) => db.name === IDB_NAME)) return [];
      } catch {
        // databases() can throw in some privacy modes; fall through to the
        // guarded open below (its onupgradeneeded abort is the safety net).
      }
    }

    return await new Promise<string[]>((resolve) => {
      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(IDB_NAME);
      } catch {
        resolve([]);
        return;
      }
      // Fires only if the DB did not already exist. Abort so we never leave a
      // store-less phantom DB behind that would break the package's open().
      request.onupgradeneeded = () => {
        try {
          request.transaction?.abort();
        } catch {
          /* ignore */
        }
      };
      request.onblocked = () => resolve([]);
      request.onerror = () => resolve([]);
      request.onsuccess = () => {
        const db = request.result;
        const ids = new Set<string>();
        try {
          if (!db.objectStoreNames.contains(IDB_STORE)) {
            db.close();
            resolve([]);
            return;
          }
          const cursorRequest = db
            .transaction(IDB_STORE, "readonly")
            .objectStore(IDB_STORE)
            .index(IDB_INDEX)
            .openKeyCursor();
          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (cursor) {
              const key = cursor.key as [string, number];
              if (Array.isArray(key) && key[0] != null) ids.add(String(key[0]));
              cursor.continue();
            } else {
              db.close();
              resolve(Array.from(ids));
            }
          };
          cursorRequest.onerror = () => {
            db.close();
            resolve(Array.from(ids));
          };
        } catch {
          try {
            db.close();
          } catch {
            /* ignore */
          }
          resolve(Array.from(ids));
        }
      };
    });
  } catch {
    return [];
  }
}

/** Distinct audioIds with bytes in either local tier (Cache Storage + IndexedDB). */
export async function listLocalAudioIds(): Promise<string[]> {
  const [cacheIds, idbIds] = await Promise.all([listCacheAudioIds(), listIdbAudioIds()]);
  return Array.from(new Set([...cacheIds, ...idbIds]));
}

/**
 * Returns true ONLY when the backend confirms this recording is fully processed
 * and durable. Any uncertainty returns false so we never clear the only local
 * copy of audio that might not be safely in GCS.
 */
async function isSafeToClear(audioId: string): Promise<boolean> {
  try {
    const response = await getNotesDetails(audioId);
    const data = response?.data as { status?: number; error_status?: number } | undefined;
    return !!data && data.status === STATUS_DONE_PROCESSING && data.error_status === 0;
  } catch {
    // 404 (id is a local-only draft / unknown), network error, etc. — keep it.
    return false;
  }
}

async function safeEstimateUsage(): Promise<number | null> {
  try {
    const estimate = await estimateStorage();
    return typeof estimate?.usage === "number" ? estimate.usage : null;
  } catch {
    return null;
  }
}

async function runRecovery(activeAudioId: string | null): Promise<QuotaRecoveryResult> {
  const usageBefore = await safeEstimateUsage();

  const all = await listLocalAudioIds();
  // Never touch the recording currently being captured.
  const candidates = all.filter((id) => id && id !== activeAudioId);

  const cleared: string[] = [];
  let skipped = 0;

  let next = 0;
  const worker = async () => {
    while (next < candidates.length) {
      const audioId = candidates[next++];
      if (!(await isSafeToClear(audioId))) {
        skipped++;
        continue;
      }
      try {
        // clearBuffers fans out to BOTH tiers (Cache Storage + IndexedDB).
        await clearBuffers(audioId);
        cleared.push(audioId);
      } catch {
        skipped++;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(STATUS_LOOKUP_CONCURRENCY, candidates.length) }, worker));

  const usageAfter = await safeEstimateUsage();
  const reclaimedBytes = usageBefore != null && usageAfter != null ? Math.max(0, usageBefore - usageAfter) : null;

  const result: QuotaRecoveryResult = {
    attempted: candidates.length,
    cleared,
    skipped,
    reclaimedBytes,
    ranSweep: true,
  };
  logStorageQuotaRecovery(result);
  return result;
}

/**
 * Reclaim local storage after a StorageQuotaError by clearing the local backup
 * of backend-confirmed done-processing recordings. Single-flight + cooldown:
 * concurrent per-chunk quota errors coalesce onto one sweep, and repeats within
 * the cooldown window are suppressed (ranSweep === false).
 */
export async function recoverFromStorageQuota(activeAudioId: string | null | undefined): Promise<QuotaRecoveryResult> {
  if (recoveryInFlight) return recoveryInFlight;

  const now = Date.now();
  if (now - lastRecoveryAt < RECOVERY_COOLDOWN_MS) {
    return {
      attempted: 0,
      cleared: [],
      skipped: 0,
      reclaimedBytes: null,
      ranSweep: false,
    };
  }
  lastRecoveryAt = now;

  recoveryInFlight = runRecovery(activeAudioId ?? null).finally(() => {
    recoveryInFlight = null;
  });
  return recoveryInFlight;
}

/** Test-only: reset the single-flight/cooldown guard between cases. */
export function __resetStorageQuotaRecoveryState(): void {
  recoveryInFlight = null;
  lastRecoveryAt = 0;
}
